/**
 * Admin Editor Store (Zustand)
 *
 * State management for the Admin Editor feature.
 * Handles app grid items, phase configurations, and sidebar organization.
 *
 * Features:
 * - Persist to localStorage for immediate availability
 * - Background sync to PostgreSQL via userSettingsService
 * - Optimistic updates with error recovery
 *
 * @example
 * ```tsx
 * import { useAdminEditorStore } from '@features/admin/store/adminEditorStore';
 *
 * function AdminEditor() {
 *   const { appGridItems, toggleAppGridItemVisibility } = useAdminEditorStore();
 *
 *   return (
 *     <GridItemList
 *       items={appGridItems}
 *       onToggleVisibility={toggleAppGridItemVisibility}
 *     />
 *   );
 * }
 * ```
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getSetting, setSetting } from '@shared/services/userSettingsService';
import { logger } from '@shared/utils/logger';
import type {
  AdminEditorStore,
  AdminEditorState,
  AppGridItem,
  PhaseConfig,
  PhaseType,
  CustomPhaseNames,
  AdminEditorSettings,
} from '../types/adminEditor';
import {
  getDefaultSettings,
  DEFAULT_APP_GRID_ITEMS,
  DEFAULT_SIDEBAR_PHASES,
  SETTINGS_VERSION,
} from '../constants/defaults';

// ============================================================================
// Constants
// ============================================================================

/** Settings namespace for PostgreSQL storage */
const SETTINGS_NAMESPACE = 'admin';

/** Settings key for PostgreSQL storage */
const SETTINGS_KEY = 'admin-editor-config';

/** localStorage key for Zustand persistence */
const STORAGE_KEY = 'admin-editor-storage';

// ============================================================================
// Initial State
// ============================================================================

const initialState: AdminEditorState = {
  appGridItems: [...DEFAULT_APP_GRID_ITEMS],
  phases: DEFAULT_SIDEBAR_PHASES.map((phase) => ({
    ...phase,
    pages: [...phase.pages],
  })),
  customPhaseNames: {},
  currentPhase: null,
  isLoading: false,
  error: null,
  isDirty: false,
};

// ============================================================================
// Store
// ============================================================================

export const useAdminEditorStore = create<AdminEditorStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ======================================================================
      // App Grid Actions
      // ======================================================================

      /**
       * Set all app grid items
       */
      setAppGridItems: (items: AppGridItem[]) => {
        set({ appGridItems: items, isDirty: true });
      },

      /**
       * Update a single app grid item
       */
      updateAppGridItem: (id: string, updates: Partial<AppGridItem>) => {
        set((state) => ({
          appGridItems: state.appGridItems.map((item) =>
            item.id === id ? { ...item, ...updates } : item
          ),
          isDirty: true,
        }));
      },

      /**
       * Reorder an app grid item to a new position
       */
      reorderAppGridItem: (id: string, newOrder: number) => {
        set((state) => {
          const items = [...state.appGridItems];
          const currentIndex = items.findIndex((item) => item.id === id);
          if (currentIndex === -1) return state;

          const [item] = items.splice(currentIndex, 1);
          items.splice(newOrder, 0, item);

          // Update all order values
          const reorderedItems = items.map((item, index) => ({
            ...item,
            order: index,
          }));

          return { appGridItems: reorderedItems, isDirty: true };
        });
      },

      /**
       * Toggle visibility of an app grid item
       */
      toggleAppGridItemVisibility: (id: string) => {
        set((state) => ({
          appGridItems: state.appGridItems.map((item) =>
            item.id === id ? { ...item, visible: !item.visible } : item
          ),
          isDirty: true,
        }));
      },

      // ======================================================================
      // Phase Actions
      // ======================================================================

      /**
       * Set all phase configurations
       */
      setPhases: (phases: PhaseConfig[]) => {
        set({ phases, isDirty: true });
      },

      /**
       * Update a single phase configuration
       */
      updatePhase: (type: PhaseType, updates: Partial<PhaseConfig>) => {
        set((state) => ({
          phases: state.phases.map((phase) =>
            phase.type === type ? { ...phase, ...updates } : phase
          ),
          isDirty: true,
        }));
      },

      /**
       * Set custom phase names
       */
      setPhaseNames: (names: CustomPhaseNames) => {
        set({ customPhaseNames: names, isDirty: true });
      },

      /**
       * Set the currently active phase for editing
       */
      setCurrentPhase: (phase: PhaseType | null) => {
        set({ currentPhase: phase });
      },

      /**
       * Toggle phase collapse state
       */
      togglePhaseCollapse: (type: PhaseType) => {
        set((state) => ({
          phases: state.phases.map((phase) =>
            phase.type === type ? { ...phase, collapsed: !phase.collapsed } : phase
          ),
          isDirty: true,
        }));
      },

      // ======================================================================
      // Page Actions
      // ======================================================================

      /**
       * Move a page to a different phase
       */
      movePage: (pageId: string, fromPhase: PhaseType, toPhase: PhaseType) => {
        if (fromPhase === toPhase) return;

        set((state) => {
          const phases = state.phases.map((phase) => ({ ...phase, pages: [...phase.pages] }));
          const fromPhaseConfig = phases.find((p) => p.type === fromPhase);
          const toPhaseConfig = phases.find((p) => p.type === toPhase);

          if (!fromPhaseConfig || !toPhaseConfig) return state;

          const pageIndex = fromPhaseConfig.pages.findIndex((p) => p.id === pageId);
          if (pageIndex === -1) return state;

          const [page] = fromPhaseConfig.pages.splice(pageIndex, 1);
          page.order = toPhaseConfig.pages.length;
          toPhaseConfig.pages.push(page);

          // Reorder remaining pages in source phase
          fromPhaseConfig.pages = fromPhaseConfig.pages.map((p, i) => ({ ...p, order: i }));

          return { phases, isDirty: true };
        });
      },

      /**
       * Reorder a page within its phase
       */
      reorderPage: (pageId: string, phase: PhaseType, newOrder: number) => {
        set((state) => {
          const phases = state.phases.map((p) => ({ ...p, pages: [...p.pages] }));
          const phaseConfig = phases.find((p) => p.type === phase);

          if (!phaseConfig) return state;

          const currentIndex = phaseConfig.pages.findIndex((p) => p.id === pageId);
          if (currentIndex === -1) return state;

          const [page] = phaseConfig.pages.splice(currentIndex, 1);
          phaseConfig.pages.splice(newOrder, 0, page);

          // Update all order values
          phaseConfig.pages = phaseConfig.pages.map((p, i) => ({ ...p, order: i }));

          return { phases, isDirty: true };
        });
      },

      /**
       * Toggle page visibility
       */
      togglePageVisibility: (pageId: string, phase: PhaseType) => {
        set((state) => ({
          phases: state.phases.map((p) =>
            p.type === phase
              ? {
                  ...p,
                  pages: p.pages.map((page) =>
                    page.id === pageId ? { ...page, visible: !page.visible } : page
                  ),
                }
              : p
          ),
          isDirty: true,
        }));
      },

      // ======================================================================
      // Persistence Actions
      // ======================================================================

      /**
       * Load settings from PostgreSQL server
       */
      loadFromServer: async () => {
        const store = get();
        if (store.isLoading) return;

        set({ isLoading: true, error: null });

        try {
          const settings = await getSetting<AdminEditorSettings>(SETTINGS_KEY, {
            namespace: SETTINGS_NAMESPACE,
            fallbackToLocal: true,
          });

          if (settings && settings.version === SETTINGS_VERSION) {
            set({
              appGridItems: settings.appGridItems,
              phases: settings.phases,
              customPhaseNames: settings.customPhaseNames,
              isLoading: false,
              isDirty: false,
            });
            logger.log('[AdminEditorStore] Loaded settings from server');
          } else if (settings && settings.version !== SETTINGS_VERSION) {
            // Handle version migration if needed
            logger.warn(
              `[AdminEditorStore] Settings version mismatch: ${settings.version} vs ${SETTINGS_VERSION}`
            );
            // For now, use defaults and mark as dirty to trigger save
            set({
              ...initialState,
              isLoading: false,
              isDirty: true,
            });
          } else {
            // No settings found, use defaults
            set({
              isLoading: false,
              isDirty: false,
            });
            logger.log('[AdminEditorStore] No server settings, using defaults');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          logger.error('[AdminEditorStore] Failed to load settings:', error);
          set({
            isLoading: false,
            error: `Failed to load settings: ${message}`,
          });
        }
      },

      /**
       * Save settings to PostgreSQL server
       */
      saveToServer: async () => {
        const store = get();
        if (store.isLoading) return false;

        set({ isLoading: true, error: null });

        try {
          const settings: AdminEditorSettings = {
            appGridItems: store.appGridItems,
            phases: store.phases,
            customPhaseNames: store.customPhaseNames,
            lastModified: new Date().toISOString(),
            version: SETTINGS_VERSION,
          };

          const success = await setSetting(SETTINGS_KEY, settings, {
            namespace: SETTINGS_NAMESPACE,
            syncToLocal: true,
          });

          if (success) {
            set({ isLoading: false, isDirty: false });
            logger.log('[AdminEditorStore] Saved settings to server');
            return true;
          } else {
            throw new Error('Save returned false');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          logger.error('[AdminEditorStore] Failed to save settings:', error);
          set({
            isLoading: false,
            error: `Failed to save settings: ${message}`,
          });
          return false;
        }
      },

      /**
       * Reset to default settings
       */
      resetToDefaults: () => {
        const defaults = getDefaultSettings();
        set({
          appGridItems: defaults.appGridItems,
          phases: defaults.phases,
          customPhaseNames: defaults.customPhaseNames,
          currentPhase: null,
          isDirty: true,
          error: null,
        });
        logger.log('[AdminEditorStore] Reset to defaults');
      },

      // ======================================================================
      // Utility Actions
      // ======================================================================

      /**
       * Reset entire store to initial state
       */
      reset: () => {
        set(initialState);
      },

      /**
       * Set loading state
       */
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      /**
       * Set error message
       */
      setError: (error: string | null) => {
        set({ error });
      },

      /**
       * Mark settings as dirty/changed
       */
      setDirty: (dirty: boolean) => {
        set({ isDirty: dirty });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist configuration state
        appGridItems: state.appGridItems,
        phases: state.phases,
        customPhaseNames: state.customPhaseNames,
        // DO NOT persist UI state (currentPhase, isLoading, error, isDirty)
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<AdminEditorState>),
        // Always start with fresh UI state
        currentPhase: null,
        isLoading: false,
        error: null,
        isDirty: false,
      }),
    }
  )
);

// ============================================================================
// Selectors (for optimized component subscriptions)
// ============================================================================

const selectVisibleAppGridItems = (() => {
  let lastItems: AppGridItem[] | null = null;
  let lastResult: AppGridItem[] = [];

  return (state: AdminEditorState) => {
    if (state.appGridItems === lastItems) {
      return lastResult;
    }

    lastItems = state.appGridItems;
    const visibleItems = state.appGridItems.filter((item) => item.visible);
    visibleItems.sort((a, b) => a.order - b.order);
    lastResult = visibleItems;
    return lastResult;
  };
})();

const selectVisiblePhases = (() => {
  let lastPhases: PhaseConfig[] | null = null;
  let lastResult: PhaseConfig[] = [];

  return (state: AdminEditorState) => {
    if (state.phases === lastPhases) {
      return lastResult;
    }

    lastPhases = state.phases;
    const visiblePhases = state.phases.map((phase) => {
      const pages = phase.pages.filter((page) => page.visible);
      pages.sort((a, b) => a.order - b.order);
      return {
        ...phase,
        pages,
      };
    });
    lastResult = visiblePhases;
    return lastResult;
  };
})();

const selectAdminEditorStatus = (() => {
  let lastIsLoading: boolean | null = null;
  let lastError: string | null = null;
  let lastIsDirty: boolean | null = null;
  let lastResult = {
    isLoading: false,
    error: null as string | null,
    isDirty: false,
  };

  return (state: AdminEditorState) => {
    if (
      state.isLoading === lastIsLoading &&
      state.error === lastError &&
      state.isDirty === lastIsDirty
    ) {
      return lastResult;
    }

    lastIsLoading = state.isLoading;
    lastError = state.error;
    lastIsDirty = state.isDirty;
    lastResult = {
      isLoading: state.isLoading,
      error: state.error,
      isDirty: state.isDirty,
    };
    return lastResult;
  };
})();

const selectAppGridActions = (() => {
  let lastResult: Pick<
    AdminEditorStore,
    'setAppGridItems' | 'updateAppGridItem' | 'reorderAppGridItem' | 'toggleAppGridItemVisibility'
  > | null = null;

  return (state: AdminEditorStore) => {
    if (
      lastResult &&
      lastResult.setAppGridItems === state.setAppGridItems &&
      lastResult.updateAppGridItem === state.updateAppGridItem &&
      lastResult.reorderAppGridItem === state.reorderAppGridItem &&
      lastResult.toggleAppGridItemVisibility === state.toggleAppGridItemVisibility
    ) {
      return lastResult;
    }

    lastResult = {
      setAppGridItems: state.setAppGridItems,
      updateAppGridItem: state.updateAppGridItem,
      reorderAppGridItem: state.reorderAppGridItem,
      toggleAppGridItemVisibility: state.toggleAppGridItemVisibility,
    };

    return lastResult;
  };
})();

const selectPhaseActions = (() => {
  let lastResult: Pick<
    AdminEditorStore,
    | 'setPhases'
    | 'updatePhase'
    | 'setPhaseNames'
    | 'setCurrentPhase'
    | 'togglePhaseCollapse'
    | 'movePage'
    | 'reorderPage'
    | 'togglePageVisibility'
  > | null = null;

  return (state: AdminEditorStore) => {
    if (
      lastResult &&
      lastResult.setPhases === state.setPhases &&
      lastResult.updatePhase === state.updatePhase &&
      lastResult.setPhaseNames === state.setPhaseNames &&
      lastResult.setCurrentPhase === state.setCurrentPhase &&
      lastResult.togglePhaseCollapse === state.togglePhaseCollapse &&
      lastResult.movePage === state.movePage &&
      lastResult.reorderPage === state.reorderPage &&
      lastResult.togglePageVisibility === state.togglePageVisibility
    ) {
      return lastResult;
    }

    lastResult = {
      setPhases: state.setPhases,
      updatePhase: state.updatePhase,
      setPhaseNames: state.setPhaseNames,
      setCurrentPhase: state.setCurrentPhase,
      togglePhaseCollapse: state.togglePhaseCollapse,
      movePage: state.movePage,
      reorderPage: state.reorderPage,
      togglePageVisibility: state.togglePageVisibility,
    };

    return lastResult;
  };
})();

const selectPersistenceActions = (() => {
  let lastResult: Pick<
    AdminEditorStore,
    'loadFromServer' | 'saveToServer' | 'resetToDefaults'
  > | null = null;

  return (state: AdminEditorStore) => {
    if (
      lastResult &&
      lastResult.loadFromServer === state.loadFromServer &&
      lastResult.saveToServer === state.saveToServer &&
      lastResult.resetToDefaults === state.resetToDefaults
    ) {
      return lastResult;
    }

    lastResult = {
      loadFromServer: state.loadFromServer,
      saveToServer: state.saveToServer,
      resetToDefaults: state.resetToDefaults,
    };

    return lastResult;
  };
})();

/**
 * Select only visible app grid items sorted by order
 * Memoized selector to keep snapshots stable across renders
 */
export const useVisibleAppGridItems = () =>
  useAdminEditorStore(selectVisibleAppGridItems);

/**
 * Select only the phases with visible pages
 * Memoized selector to keep snapshots stable across renders
 */
export const useVisiblePhases = () =>
  useAdminEditorStore(selectVisiblePhases);

/**
 * Select phase by type
 */
export const usePhase = (type: PhaseType) =>
  useAdminEditorStore((state) => state.phases.find((p) => p.type === type));

/**
 * Select loading and error state
 * Memoized selector to keep snapshots stable across renders
 */
export const useAdminEditorStatus = () =>
  useAdminEditorStore(selectAdminEditorStatus);

/**
 * Select app grid actions only
 * Memoized selector to keep snapshots stable across renders
 */
export const useAppGridActions = () =>
  useAdminEditorStore(selectAppGridActions);

/**
 * Select phase actions only
 * Memoized selector to keep snapshots stable across renders
 */
export const usePhaseActions = () =>
  useAdminEditorStore(selectPhaseActions);

/**
 * Select persistence actions only
 * Memoized selector to keep snapshots stable across renders
 */
export const usePersistenceActions = () =>
  useAdminEditorStore(selectPersistenceActions);

export default useAdminEditorStore;
